import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const EDGE_FUNCTION_URL = "https://zfdiziwbgzakgbtrlbpq.supabase.co/functions/v1/smart-function";

const helpOptions = [
  { value: "new-website", label: "New website" },
  { value: "website-redesign", label: "Website redesign" },
  { value: "google-ads", label: "Google Ads" },
  { value: "seo", label: "SEO" },
  { value: "other", label: "Other" },
];

interface LeadPayload {
  email: string;
  name: string;
  phone: string;
  help_with: string;
  business_name: string;
  website: string | null;
  message: string;
  source: string;
  company: string;
}

async function submitLead(payload: LeadPayload): Promise<{ success: boolean }> {
  const response = await fetch(EDGE_FUNCTION_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });

  let data;
  try {
    data = await response.json();
  } catch {
    throw new Error("Failed to parse response. Please try again.");
  }

  if (!response.ok) {
    throw new Error(data?.error || "Something went wrong. Please try again.");
  }

  return data;
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export const LeadForm = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [helpWith, setHelpWith] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    business_name: "",
    website: "",
    message: "",
    company: "", // honeypot
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      business_name: "",
      website: "",
      message: "",
      company: "",
    });
    setHelpWith("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");

    // Trim all values
    const name = formData.name.trim();
    const email = formData.email.trim();
    const phone = formData.phone.trim();
    const business_name = formData.business_name.trim();
    const website = formData.website.trim() || null;
    const message = formData.message.trim();
    const company = formData.company.trim();
    const source = window.location.pathname;

    // Get display label for help_with
    const selectedOption = helpOptions.find((opt) => opt.value === helpWith);
    const help_with = selectedOption?.label || helpWith;

    // Validation
    if (!email) {
      setErrorMessage("Email is required.");
      return;
    }
    if (!isValidEmail(email)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }
    if (!phone) {
      setErrorMessage("Phone is required.");
      return;
    }
    if (!name) {
      setErrorMessage("Full Name is required.");
      return;
    }
    if (!helpWith) {
      setErrorMessage("Please select what we can help with.");
      return;
    }
    if (!business_name) {
      setErrorMessage("Business Name is required.");
      return;
    }
    if (!message) {
      setErrorMessage("Message is required.");
      return;
    }

    setIsSubmitting(true);

    // Honeypot check: if filled, silently succeed without calling API
    if (company) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      resetForm();
      setIsSubmitting(false);
      navigate("/thank-you");
      return;
    }

    const payload: LeadPayload = {
      email,
      name,
      phone,
      help_with,
      business_name,
      website,
      message,
      source,
      company,
    };

    try {
      await submitLead(payload);
      resetForm();
      navigate("/thank-you");
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Something went wrong. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Honeypot field - hidden from users */}
      <div className="absolute -left-[9999px] opacity-0" aria-hidden="true">
        <Label htmlFor="company">Company</Label>
        <Input
          id="company"
          name="company"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={formData.company}
          onChange={handleInputChange}
        />
      </div>

      {/* Required Fields */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="name">Full Name *</Label>
          <Input
            id="name"
            name="name"
            type="text"
            required
            maxLength={100}
            value={formData.name}
            onChange={handleInputChange}
            placeholder="John Smith"
            disabled={isSubmitting}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            name="email"
            type="email"
            required
            maxLength={255}
            value={formData.email}
            onChange={handleInputChange}
            placeholder="john@example.com"
            disabled={isSubmitting}
          />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="phone">Phone *</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            required
            maxLength={20}
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="(555) 123-4567"
            disabled={isSubmitting}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="help_with">What can we help with? *</Label>
          <Select value={helpWith} onValueChange={setHelpWith} required disabled={isSubmitting}>
            <SelectTrigger id="help_with">
              <SelectValue placeholder="Select an option…" />
            </SelectTrigger>
            <SelectContent>
              {helpOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Business Name (Required) + Current Website (Optional) */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="business_name">Business Name *</Label>
          <Input
            id="business_name"
            name="business_name"
            type="text"
            required
            maxLength={100}
            value={formData.business_name}
            onChange={handleInputChange}
            placeholder="Green Lawn Pros LLC"
            disabled={isSubmitting}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="website">Current Website</Label>
          <Input
            id="website"
            name="website"
            type="url"
            maxLength={255}
            value={formData.website}
            onChange={handleInputChange}
            placeholder="https://yoursite.com"
            disabled={isSubmitting}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">Message *</Label>
        <Textarea
          id="message"
          name="message"
          required
          maxLength={1000}
          rows={3}
          value={formData.message}
          onChange={handleInputChange}
          placeholder="Tell us about your project or what you're looking for..."
          disabled={isSubmitting}
        />
      </div>


      {/* Error Message */}
      {errorMessage && (
        <div className="rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-sm text-destructive">
          {errorMessage}
        </div>
      )}

      <Button
        type="submit"
        size="lg"
        className="w-full bg-primary-dark hover:bg-[hsl(124,55%,18%)] text-white font-semibold py-6 rounded-xl shadow-md hover:shadow-lg hover:-translate-y-[1px] active:translate-y-0 active:shadow-md transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary/30 disabled:bg-primary/40 disabled:cursor-not-allowed disabled:shadow-none disabled:translate-y-0 min-h-[48px]"
        disabled={isSubmitting || !helpWith}
      >
        {isSubmitting ? "Submitting…" : "Get Started"}
      </Button>
    </form>
  );
};
